import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import {
  USER_MISSIONS_QUERY_KEY,
  useAssignUserMission,
  useMissionCatalog,
  useUserMissionStats,
  useUserMissions
} from '@/hooks/useUserMissions';
import PersonContext from '@/pages/Person/PersonContext';
import type { MissionEligibility, UserMission, UserMissionState } from '@/types/userMission';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Paginator,
  Pill,
  Select,
  TabSwitch,
  TabSwitchList,
  TabSwitchTab,
  Typography
} from '@/ui';
import Container from '@/ui/components/Container';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import MissionGroupCard from '@/ui/components/MissionGroupCard';
import UserMissionRow from '@/ui/components/UserMissionRow';
import { cn } from '@/ui/lib/utils';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { formatTimeLeft } from '@/utils/userMissions';

type EligibilitySection = { order: number; title: string };

// Dropdown sections, top to bottom: missions the picker would pick first.
const ELIGIBILITY_SECTIONS: Record<MissionEligibility, EligibilitySection> = {
  eligible: { order: 0, title: 'Eligible' },
  already_assigned: { order: 1, title: 'Not eligible: member already has it' },
  cannot_complete: { order: 2, title: "Not eligible: can't complete right now" },
  not_for_program: { order: 3, title: "Not eligible: not for this member's program" },
  no_program: { order: 4, title: 'Not eligible: member has no program' }
};

// A code the API added after this page shipped lands last instead of crashing.
const OTHER_SECTION: EligibilitySection = { order: 99, title: 'Not eligible' };
const sectionFor = (eligibility: MissionEligibility) =>
  ELIGIBILITY_SECTIONS[eligibility] ?? OTHER_SECTION;

const TABS: UserMissionState[] = ['current', 'history'];

const formatPercent = (rate: number | null) => (rate === null ? '—' : `${Math.round(rate * 100)}%`);

const catalogKey = (missionType: string, missionId: string) => `${missionType}:${missionId}`;

const byEndingSoonest = (a: UserMission, b: UserMission) =>
  (a.expired_at ? Date.parse(a.expired_at) : Infinity) -
  (b.expired_at ? Date.parse(b.expired_at) : Infinity);

// Darkens with its tab when selected; the tab is a `group` and Headless UI
// marks it data-selected.
const TabCount = ({ count }: { count?: number }) =>
  count === undefined ? null : (
    <Pill
      size="sm"
      color="neutral"
      className={cn(
        'ml-1',
        'group-data-[selected]:bg-uniform-gray-900 group-data-[selected]:text-white',
        'dark:group-data-[selected]:bg-uniform-gray-100 dark:group-data-[selected]:text-uniform-gray-900'
      )}>
      {count.toLocaleString()}
    </Pill>
  );

const PersonMissions = () => {
  const { person } = useContext(PersonContext);
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: UserMissionState = searchParams.get('tab') === 'history' ? 'history' : 'current';
  const [pages, setPages] = useState<Record<UserMissionState, number>>({
    current: 1,
    history: 1
  });
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Both load up front so each tab label can show its count.
  const current = useUserMissions(person.id, 'current', pages.current);
  const history = useUserMissions(person.id, 'history', pages.history);
  const activeList = tab === 'current' ? current : history;

  // Current missions, split as the app shows them.
  // "Now" is when the missions were fetched, so the reset time matches the data.
  const now = useMemo(() => new Date(current.dataUpdatedAt || Date.now()), [current.dataUpdatedAt]);
  const currentMissions = useMemo(() => current.data?.results ?? [], [current.data]);
  const daily = currentMissions.filter((userMission) => userMission.recurrency === 'Daily');
  const weekly = currentMissions
    .filter((userMission) => userMission.recurrency !== 'Daily')
    .sort(byEndingSoonest);
  const dailyResetsAt = daily
    .map((userMission) => userMission.expired_at)
    .filter(
      (expiredAt): expiredAt is string => !!expiredAt && Date.parse(expiredAt) > now.getTime()
    )
    .sort()[0];
  const renderRow = (userMission: UserMission, inGroup: boolean) => (
    <UserMissionRow
      key={userMission.id}
      userMission={userMission}
      memberTimeZone={person.time_zone}
      showRecurrency={!inGroup}
    />
  );

  const selectTab = (next: UserMissionState) =>
    setSearchParams(
      (params) => {
        if (next === 'current') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true }
    );
  const catalog = useMissionCatalog(person.id, isAssigning);
  const stats = useUserMissionStats(person.id, tab === 'history');

  const catalogOptions = useMemo(
    () =>
      [...(catalog.data ?? [])]
        // Stable sort keeps the API's daily-then-weekly order within a section.
        .sort((a, b) => sectionFor(a.eligibility).order - sectionFor(b.eligibility).order)
        .map((item) => ({
          value: catalogKey(item.mission_type, item.mission_id),
          label: `${item.recurrency} · ${item.title}`,
          subLabel: item.kind,
          group: sectionFor(item.eligibility).title,
          disabled: item.eligibility !== 'eligible'
        })),
    [catalog.data]
  );
  const selectedItem = catalog.data?.find(
    (item) => catalogKey(item.mission_type, item.mission_id) === selectedKey
  );

  const closeDrawer = () => {
    setIsAssigning(false);
    setSelectedKey(null);
  };

  const { mutate: assign, isPending: isSaving } = useAssignUserMission({
    onSuccess: (userMission) => {
      queryClient.invalidateQueries({ queryKey: [USER_MISSIONS_QUERY_KEY, person.id] });
      addMessage({ level: 'success', text: `Assigned "${userMission.title}"` });
      closeDrawer();
      selectTab('current');
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });

  return (
    <Container type="page">
      <PageHeader headingText="Missions" className="sr-only" />

      <div className="flex max-w-4xl flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <TabSwitch selectedIndex={TABS.indexOf(tab)} onChange={(index) => selectTab(TABS[index])}>
            <TabSwitchList>
              <TabSwitchTab className="group">
                Current <TabCount count={current.data?.count} />
              </TabSwitchTab>
              <TabSwitchTab className="group">
                History <TabCount count={history.data?.count} />
              </TabSwitchTab>
            </TabSwitchList>
          </TabSwitch>
          <Button leftIcon={faPlus} onClick={() => setIsAssigning(true)}>
            Assign mission
          </Button>
        </div>

        {tab === 'history' && (
          <Card data-testid="mission-stats">
            <Typography variant="label" muted as="h2">
              Completion rate · last {stats.data?.window_days ?? 30} days
            </Typography>
            {stats.isPending ? (
              <LoadingSkeleton />
            ) : stats.isError ? (
              <Typography muted>Couldn&apos;t load the completion rate.</Typography>
            ) : (
              <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
                <Typography variant="title" as="p" className="tabular-nums">
                  {formatPercent(stats.data.completion_rate)}
                </Typography>
                <Typography muted>
                  {stats.data.total
                    ? `${stats.data.completed.toLocaleString()} of ${stats.data.total.toLocaleString()} missions finished`
                    : 'No missions ended in this window.'}
                </Typography>
              </div>
            )}
          </Card>
        )}

        {activeList.isPending ? (
          <LoadingSkeleton />
        ) : activeList.isError ? (
          <Alert
            variant="error"
            message={
              tab === 'current'
                ? "Couldn't load current missions."
                : "Couldn't load mission history."
            }
          />
        ) : !activeList.data.results.length ? (
          <Card>
            <Typography muted>
              {tab === 'current' ? 'No current missions.' : 'No past missions.'}
            </Typography>
          </Card>
        ) : tab === 'current' ? (
          <>
            {daily.length > 0 && (
              <MissionGroupCard
                title="Daily"
                count={daily.length}
                aside={
                  dailyResetsAt && `Resets in ${formatTimeLeft(new Date(dailyResetsAt), now)}`
                }>
                {daily.map((userMission) => renderRow(userMission, true))}
              </MissionGroupCard>
            )}
            {weekly.length > 0 && (
              <MissionGroupCard title="Weekly" count={weekly.length}>
                {weekly.map((userMission) => renderRow(userMission, true))}
              </MissionGroupCard>
            )}
          </>
        ) : (
          <Card padding="none" className="overflow-hidden">
            <ul className="divide-y divide-uniform-gray-100 dark:divide-uniform-gray-800">
              {activeList.data.results.map((userMission) => renderRow(userMission, false))}
            </ul>
          </Card>
        )}

        {activeList.data && activeList.data.total_pages > 1 && (
          <Paginator
            currentPage={pages[tab]}
            totalPages={activeList.data.total_pages}
            onPageChange={(page) => setPages((prev) => ({ ...prev, [tab]: page }))}
          />
        )}
      </div>

      <Drawer open={isAssigning} onClose={closeDrawer} title="Assign mission">
        <div className="flex flex-col gap-3">
          <Typography variant="caption" muted as="p">
            The mission starts today in the member&apos;s time zone and pays out like any other
            mission when claimed. It counts toward their daily or weekly missions, and can go past
            the usual limit. Only missions the member is eligible for can be assigned.
          </Typography>

          {catalog.isPending ? (
            <LoadingSkeleton />
          ) : catalog.isError ? (
            <Alert variant="error" message="Couldn't load the mission catalog." />
          ) : (
            <Select
              label="Mission"
              options={catalogOptions}
              placeholder="Choose a mission"
              selectedOption={catalogOptions.find((option) => option.value === selectedKey) ?? null}
              onChange={(option) => setSelectedKey(option?.value ?? null)}
            />
          )}

          {selectedItem?.description && (
            <Typography variant="caption" muted as="p">
              {selectedItem.description}
            </Typography>
          )}

          <Button
            disabled={!selectedItem}
            isLoading={isSaving}
            onClick={() =>
              selectedItem &&
              assign({
                person: person.id,
                mission_type: selectedItem.mission_type,
                mission_id: selectedItem.mission_id
              })
            }>
            Assign
          </Button>
        </div>
      </Drawer>
    </Container>
  );
};

export default PersonMissions;
